import { useRef } from "react";
import { Box, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import EditableTableContainer, { EditableTableContainerApi } from "@arelia-components/editable-table/EditableTableContainer";
import { formatCurrency } from "@arelia-utils/helpers/formatCurrency";
import { Revenue, RevenueOverview } from "@arelia-types/earnings";
import { ChangeCardStatus } from "@arelia-components/ui-atoms/ChangeCardStatus";
import calcDifference from "@arelia-utils/helpers/calcDifference";
import { ColumnDef } from "@arelia-components/ui-atoms/data-grid";
import { FormEditActions } from "@arelia-components/form/FormEditActions";

export interface RevenueEditDialogProps<T> {
  open: boolean;
  title: string;
  data: RevenueOverview;
  apiRoute: string;
  columnConfig: ColumnDef[];

  onClose: () => void;
  onTotalsRefresh: () => void;
  getApiResponseMapper?: (data: Revenue[]) => any[];
  postApiResponseMapper?: (rowData: any[]) => Revenue[];
  headerComponent?: JSX.Element | undefined;
  selectedPeriod?: T;
  selectedPeriodDisplay?: string;
}

export function RevenueEditDialog<T>({
  open,
  title,
  data,
  apiRoute,
  columnConfig,
  onClose,
  onTotalsRefresh,
  getApiResponseMapper,
  postApiResponseMapper,
  headerComponent,
  selectedPeriod,
  selectedPeriodDisplay
}: RevenueEditDialogProps<T>) {
  const apiRef = useRef<EditableTableContainerApi>();

  const onAddRow = () => apiRef.current?.addRow();
  const onSave = () => apiRef.current?.save();

  return (
    <Dialog onClose={onClose} open={open} maxWidth={"md"}>
      <DialogTitle width={750} sx={{ marginBottom: "20px", display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: 'flex', gap: "6px", alignItems: "center" }}>
          {title}
          {headerComponent ?? <></>}
        </Box>
        <FormEditActions handleReset={onClose} handleSubmit={onSave}/>
      </DialogTitle>
      <DialogContent>
        <Typography variant="h2" sx={{ textAlign: "center" }}>{formatCurrency(data?.total || 0)} Total</Typography>
        <ChangeCardStatus change={calcDifference(data?.totalThisPeriod || 0, data?.totalPrevPeriod || 0)} sx={{ justifyContent: 'center' }} timespan={selectedPeriodDisplay} />
        <Box sx={{ marginTop: "42px" }}>
          <EditableTableContainer
            columnConfig={columnConfig as any[]}
            apiRef={apiRef}
            onTotalsRefresh={onTotalsRefresh}
            apiRoute={apiRoute}
            apiParamKey={selectedPeriod as string}
            getApiResponseMapper={getApiResponseMapper}
            postApiResponseMapper={postApiResponseMapper}
            onAddRow={onAddRow}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}